import React, { useRef, useState } from 'react';
import {
  filterFieldFormat,
  formatOptionsForSelect,
  getSelectedAdvanceInputFilter,
  getDateKeys,
} from '../../../utils/filters-util';
import { ColumnInfo, DateRangePicker, SelectItem } from '../../../common';
import styles from './RenderFilters.module.scss';
import { DATE_FORMATS } from '../../../constants/date-constants';
import { FILTER_DISPLAY_VALUE, FILTER_TYPES } from '../../../constants/filters-constants';
import toast from 'react-hot-toast';
import { filterBySlug, getAdvanceKey, isObject } from '../../../utils';
import FloatingSelect from '../../../common/floating-select/FloatingSelect';
import FloatingInput from '../../../common/floating-input/FloatingInput';
import Search from '../../../common/search/Search';
import PatientElasticSearchDropdown from '../patient-elastic-search-dropdown/PatientElasticSearchDropdown';
import { useDispatch } from 'react-redux';

const RenderFilters = ({
  item,
  filtersObject = {},
  setFiltersObject = () => {},
  handleFiltersChange = () => {},
  loading = false,
  setSelectedAdvanceInput = () => {},
  isRegularFilters = false,
}) => {
  const inputRef = useRef(null);
  const [show, setShow] = useState(false);
  const dispatch = useDispatch();

  const handleBlur = () => setShow(false);
  const handleFocus = () => {
    setShow(true);
  };

  const onSearch = (object = filtersObject, onEnter = false) => {
    setFiltersObject({ ...object });
    !!onEnter && handleFiltersChange({ ...object });
  };

  const handleSelectValueChange = ({ value = [], searchKey = '', mode = 'single', setAsSlug = false }) => {
    filtersObject.page && delete filtersObject.page;
    let filterValue = '';
    if (mode === 'multiple') {
      filterValue = value.map((item) => item?.id)?.join(',');
    } else if (mode === 'single') {
      filterValue = setAsSlug ? value?.slug : value?.id;
    }
    const obj = { ...filtersObject, [searchKey]: `${filterValue}` };
    onSearch(obj, isRegularFilters);
  };

  const handleInputValueChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue.includes('#')) {
      return toast.error('# is not allowed in input field!');
    }
    filtersObject.page && delete filtersObject.page;
    setFiltersObject({
      ...filtersObject,
      [item.key]: inputValue,
    });
  };

  const handleInputKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (item?.toLowerCase) {
        const obj = {
          ...filtersObject,
          [item.key]: item?.inputType === 'number' ? filtersObject[item.key] : filtersObject[item.key]?.toLowerCase(),
        };
        onSearch(obj, !item?.isPatientElasticSearch);
      } else {
        onSearch({ ...filtersObject }, !item?.isPatientElasticSearch);
      }
    }
  };

  const getFilterType = () => {
    switch (item.type) {
      case FILTER_TYPES.DROP_DOWN:
        const isElastic = isObject(item?.options);
        const searchQuery = isElastic ? item?.options?.search || '' : '';
        const options = isElastic
          ? [
              ...new Map(
                Object.values(item?.options || {})
                  .flat()
                  .map((option) => [option.id, option]),
              ).values(),
            ]
          : item.options;
        const selectedIds = filtersObject[item.key] ? filtersObject[item.key]?.split(',') : [];
        const selected = options?.filter((option) => {
          return selectedIds.find((selectedItem) => {
            if (item.setAsSlug ? selectedItem === option.slug : selectedItem == option.id) {
              return option;
            }
          });
        });

        return (
          <FloatingSelect
            label={item?.label ?? ''}
            placeholder={item?.placeholder ?? ''}
            mode={item?.mode}
            singleSelect={item?.mode !== 'multiple'}
            onFocus={(value) => item?.func && dispatch(item?.func({ value }))}
            showSlug={item?.showSlug}
            showSearch
            searchType={item?.searchType}
            value={formatOptionsForSelect({ list: selected, setAsSlug: item?.setAsSlug })}
            options={
              [undefined, null]?.includes(isElastic ? item?.options?.[searchQuery] : item?.options)
                ? null
                : formatOptionsForSelect({
                    list: isElastic ? item?.options?.[searchQuery] || [] : item?.options,
                    setAsSlug: item.setAsSlug,
                    useSlug: item?.showSlug,
                    searchType: item?.searchType,
                  })
            }
            small_height
            allowClear={true}
            filterOption={filterBySlug}
            isElastic={isElastic}
            handleChange={(value) => {
              handleSelectValueChange({
                value: value,
                searchKey: item.key,
                mode: 'multiple',
                setAsSlug: item?.setAsSlug ? item?.setAsSlug : false,
              });
            }}
            onClear={() => {
              delete filtersObject[item.key];
              onSearch(filtersObject, isRegularFilters);
            }}
          />
        );
      case FILTER_TYPES.DATE:
        const picker = item?.pickerLevel ? item?.pickerLevel : 'date';
        const dateKeys = getDateKeys(item);
        return (
          <DateRangePicker
            placement={'bottomLeft'}
            label={item?.label}
            allowEmpty={[false, false]}
            placeholder={item?.placeholder}
            rangePresets={item?.rangePresets}
            showRangePreset={item?.showRangePreset}
            startDate={filtersObject[dateKeys.startKey]}
            endDate={filtersObject[dateKeys.endKey]}
            picker={picker}
            showTime={false}
            filterSelected={!!filtersObject?.[dateKeys.startKey]}
            loading={loading}
            small_height
            onChange={(startDate, endDate) => {
              const obj = {
                ...filtersObject,
                [dateKeys.startKey]: startDate?.startOf(picker).format(DATE_FORMATS.timePayloadFormat),
                [dateKeys.endKey]: endDate?.endOf(picker).format(DATE_FORMATS.timePayloadFormat),
              };
              obj.page && delete obj.page;
              setFiltersObject(obj);
              if (startDate && endDate) {
                onSearch(obj, isRegularFilters);
              }
            }}
            onClear={() => {
              delete filtersObject[dateKeys.startKey];
              delete filtersObject[dateKeys.endKey];
              onSearch(filtersObject, isRegularFilters);
            }}
            format={item?.format}
          />
        );
      case FILTER_TYPES.ADVANCE_INPUT:
        const selectedAdvanceInput = getSelectedAdvanceInputFilter(filtersObject, item);
        const renderString = `${item?.placeholder ?? item?.label}: ${FILTER_DISPLAY_VALUE(selectedAdvanceInput.value)?.[getAdvanceKey(selectedAdvanceInput?.key)]}`;
        return (
          <SelectItem
            style={{ color: selectedAdvanceInput?.key ? 'black' : '' }}
            title={selectedAdvanceInput?.key ? renderString : (item?.placeholder ?? item?.label ?? 'Enter Input')}
            onClick={() =>
              setSelectedAdvanceInput({
                ...item,
                selectedKey: selectedAdvanceInput.key,
                selectedValue: selectedAdvanceInput.value,
              })
            }
          />
        );
      default:
        return (
          <div style={{ position: 'relative' }}>
            <div ref={inputRef}>
              {item?.isPrimaryFilter ? (
                <Search
                  value={filterFieldFormat(filtersObject[item.key || ''])}
                  placeholder={item?.placeholder ?? item?.label ?? ''}
                  type={item?.inputType ?? 'text'}
                  allowClear={false}
                  onChange={handleInputValueChange}
                  onFocus={handleFocus}
                  onClick={handleFocus}
                  onKeyDown={handleInputKeydown}
                />
              ) : (
                <FloatingInput
                  value={filterFieldFormat(filtersObject[item.key || ''])}
                  label={item?.label ?? ''}
                  onFocus={handleFocus}
                  placeholder={item?.placeholder ?? ''}
                  type={item?.inputType ?? 'text'}
                  allowClear={true}
                  extra_small_height
                  onClick={handleFocus}
                  onChange={handleInputValueChange}
                  onKeyDown={handleInputKeydown}
                />
              )}
            </div>

            {item?.isPatientElasticSearch && (
              <PatientElasticSearchDropdown
                inputRef={inputRef}
                show={show}
                handleBlur={handleBlur}
                isRegularFilters={isRegularFilters}
                item={item}
                filtersObject={filtersObject}
                onSearch={onSearch}
                isCustomUserSearch={item?.isCustomUserSearch}
                fetchListingFunc={item?.fetchListingFunc}
                queryKey={item?.queryKey}
              />
            )}
          </div>
        );
    }
  };

  return (
    <div className={styles.filterField}>
      {getFilterType()}
      {item?.description && <ColumnInfo description={item.description} className={styles.filterInfo} />}
    </div>
  );
};

export default RenderFilters;
